import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import type { ActivityRecurrence, ActivityTargetType } from "./activitiesModel";

export interface CreateActivityInput {
  targetType: ActivityTargetType;
  targetPetId: string | null;
  targetPackId: string | null;
  typeId: string;
  scheduledTime: string | null;
  description: string | null;
  assignedTo: string | null;
  scheduledDate: string;
  recurrence: ActivityRecurrence | null;
}

const recurrenceRow = (activityId: string, r: ActivityRecurrence) => ({
  stay_activity_id: activityId,
  frequency: r.frequency,
  interval_days: r.frequency === "every_n_days" ? r.intervalDays : null,
  days_of_week: r.frequency === "weekly" ? r.daysOfWeek : null,
  days_of_month: r.frequency === "monthly" ? r.daysOfMonth : null,
  ends_on: r.endsOn,
});

export interface UpdateActivityInput {
  activityId: string;
  typeId: string;
  scheduledTime: string | null;
  description: string | null;
  assignedTo: string | null;
  recurrence: ActivityRecurrence | null;
}

export function buildActivityUpdate(input: UpdateActivityInput) {
  return {
    stay_activity_type_id: input.typeId,
    scheduled_time: input.scheduledTime,
    description: input.description,
    assigned_to: input.assignedTo,
  };
}

export function buildActivityDisable() {
  return { is_active: false };
}

export function useCreateStayActivity() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (input: CreateActivityInput) => {
      if (!clinicId) throw new Error("No clinic selected");
      const insert = {
        clinic_id: clinicId,
        stay_activity_type_id: input.typeId,
        target_type: input.targetType,
        target_pet_id: input.targetType === "pet" ? input.targetPetId : null,
        target_stay_pack_id: input.targetType === "pack" ? input.targetPackId : null,
        scheduled_time: input.scheduledTime,
        scheduled_date: input.scheduledDate,
        description: input.description,
        assigned_to: input.assignedTo,
      };
      const { data, error } = await supabase
        .from("stay_activities")
        // scheduled_date ainda não está em types.ts (regenerado fora do worktree).
        .insert(insert as never)
        .select("id")
        .single();
      if (error) throw error;
      if (input.recurrence) {
        const { error: recErr } = await supabase
          .from("stay_activity_recurrence")
          .insert(recurrenceRow((data as { id: string }).id, input.recurrence));
        if (recErr) throw recErr;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stay-activities"] });
    },
  });
}

export function useUpdateStayActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateActivityInput) => {
      const { error } = await supabase
        .from("stay_activities")
        .update(buildActivityUpdate(input) as never)
        .eq("id", input.activityId);
      if (error) throw error;

      const { error: deleteErr } = await supabase
        .from("stay_activity_recurrence")
        .delete()
        .eq("stay_activity_id", input.activityId);
      if (deleteErr) throw deleteErr;

      if (input.recurrence) {
        const { error: recErr } = await supabase
          .from("stay_activity_recurrence")
          .insert(recurrenceRow(input.activityId, input.recurrence));
        if (recErr) throw recErr;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stay-activities"] });
    },
  });
}

export function useDeleteStayActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase
        .from("stay_activities")
        .update(buildActivityDisable() as never)
        .eq("id", activityId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stay-activities"] });
    },
  });
}

export function useToggleActivityCompletion() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();

  return useMutation({
    mutationFn: async ({ activityId, day, done }: { activityId: string; day: string; done: boolean }) => {
      if (done) {
        const { error } = await supabase
          .from("stay_activity_completions")
          .upsert(
            { stay_activity_id: activityId, completed_date: day, completed_by: profile?.id ?? null },
            { onConflict: "stay_activity_id,completed_date" },
          );
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("stay_activity_completions")
          .delete()
          .eq("stay_activity_id", activityId)
          .eq("completed_date", day);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stay-activities"] });
    },
  });
}
